import * as THREE from "three";
import Line from "./Line.js";
import { createFaceGeometry, grow, combineGeometryAttributes, pairs, setWorldMatrix, } from "./geometry.js";
export default class Face {
    #id;
    #polyhedron;
    #edges;
    #plane;
    #color;
    constructor(id, data, polyhedron) {
        this.#id = id;
        this.#polyhedron = polyhedron;
        this.#color = data.color;
        this.#edges = new Map();
        for (const [edgeId, sign] of Object.entries(data.edges)) {
            this.#edges.set(edgeId, sign);
        }
        this.#plane = new THREE.Vector4().fromArray(data.plane);
    }
    get id() {
        return this.#id;
    }
    get color() {
        return this.#color;
    }
    set color(value) {
        this.#color = value;
    }
    getData() {
        return {
            edges: Object.fromEntries(Array.from(this.#edges).map(([id, sign]) => [id, sign])),
            plane: this.#plane.toArray(),
            color: this.#color,
        };
    }
    toString() {
        let chunks = [];
        for (const [edgeId, sign] of this.#edges) {
            if (sign > 0) {
                chunks.push(`+${edgeId}`);
            }
            else {
                chunks.push(`-${edgeId}`);
            }
        }
        if (this.#color) {
            chunks.push(`color=${this.#color}`);
        }
        return chunks.join(",");
    }
    *edges() {
        for (const [edgeId, sign] of this.#edges.entries()) {
            yield [this.#polyhedron.getEdge(edgeId), sign];
        }
    }
    get nEdges() {
        return this.#edges.size;
    }
    get plane() {
        return this.#plane;
    }
    hasTheSameEdges(edges) {
        if (edges.size !== this.#edges.size) {
            return false;
        }
        for (const [edgeId, sign] of this.#edges.entries()) {
            if (edges.get(edgeId) !== sign) {
                return false;
            }
        }
        return true;
    }
    getIntersectionLine(plane) {
        const line = Line.fromPlanes(this.#plane, plane);
        if (line.norm < 1e-6) {
            return undefined;
        }
        return line;
    }
    getNormal() {
        const v = new THREE.Vector3(this.#plane.x, this.#plane.y, this.#plane.z);
        const n = v.length();
        if (n < 1e-6) {
            throw new Error("Faces is contained in a plane at infinity");
        }
        return v.multiplyScalar(1 / n);
    }
    getEdgeSign(id) {
        return this.#edges.get(id) ?? 0;
    }
    getCenter() {
        const center = new THREE.Vector3();
        for (const [edge] of this.edges()) {
            const P = this.getFirstVertex(edge).toAffine();
            center.add(P);
        }
        return center.multiplyScalar(1 / this.nEdges);
    }
    findAdjecentEdge(face) {
        for (const [edge] of this.edges()) {
            const sign = face.getEdgeSign(edge.id);
            if (sign !== 0) {
                return edge;
            }
        }
        return undefined;
    }
    alignObject3D(object, edge) {
        const sign = this.getEdgeSign(edge.id);
        let v0;
        let v1;
        if (sign > 0) {
            [v0, v1] = edge.vertices;
        }
        else {
            [v1, v0] = edge.vertices;
        }
        const P0 = v0.toAffine();
        const P1 = v1.toAffine();
        const center = new THREE.Vector3().copy(P0).add(P1).multiplyScalar(0.5);
        const parallel = new THREE.Vector3().copy(P1).sub(P0).normalize();
        const normal = this.getNormal();
        const tangent = new THREE.Vector3().copy(parallel).cross(normal).normalize();
        setWorldMatrix(object, 
        // prettier-ignore
        new THREE.Matrix4(parallel.x, normal.x, tangent.x, center.x, parallel.y, normal.y, tangent.y, center.y, parallel.z, normal.z, tangent.z, center.z, 0, 0, 0, 1));
    }
    getEdgeLoops() {
        const edgesByVertexId = new Map();
        for (const [edge, sign] of this.edges()) {
            if (edge.nVertices === 2) {
                const [v0, v1] = edge.vertices;
                if (sign > 0) {
                    edgesByVertexId.set(v0.id, edge);
                }
                else {
                    edgesByVertexId.set(v1.id, edge);
                }
            }
        }
        const loops = [];
        while (edgesByVertexId.size > 0) {
            const loop = [];
            let id = edgesByVertexId.keys().next().value;
            let edge = edgesByVertexId.get(id);
            while (edge) {
                loop.push(edge);
                edgesByVertexId.delete(id);
                const [v0, v1] = edge.vertices;
                if (v0.id === id) {
                    id = v1.id;
                }
                else {
                    id = v0.id;
                }
                edge = edgesByVertexId.get(id);
            }
            loops.push(loop);
        }
        return loops;
    }
    getFirstVertex(edge) {
        const sign = this.getEdgeSign(edge.id);
        if (sign === 0) {
            throw new Error("Edge is not part of the face");
        }
        if (sign > 0) {
            return edge.vertices[0];
        }
        return edge.vertices[1];
    }
    getVertexLoops() {
        const loops = this.getEdgeLoops();
        return loops.map((loop) => {
            return loop.map((edge) => {
                return this.getFirstVertex(edge);
            });
        });
    }
    *vertices() {
        const loops = this.getVertexLoops();
        for (const loop of loops) {
            for (const vertex of loop) {
                yield vertex;
            }
        }
    }
    getFaceGeometry(options = {}) {
        const { edgeWidth, noBevel } = options;
        return createFaceGeometry(this.getFaceGeometryAttributes({ edgeWidth, noBevel }));
    }
    getFaceGeometryAttributes(options = {}) {
        // TODO: Add support for non-convex faces.
        const { edgeWidth = 0, noBevel = false } = options;
        const position = [];
        const normal = [];
        const index = [];
        const skinIndex = [];
        const skinWeight = [];
        const faceBindings = [];
        const vertexBindings = [];
        const loops = this.getVertexLoops();
        if (loops.length === 0) {
            throw new Error("Face has no edge loops");
        }
        if (loops.length > 1) {
            throw new Error("Face has multiple edge loops");
        }
        const n = this.getNormal();
        let loop = loops[0].map((v) => v.toAffine());
        if (edgeWidth > 0) {
            loop = Array.from(grow(loop, n, -edgeWidth));
        }
        if (loop.length < 3) {
            throw new Error("Face has less than 3 vertices");
        }
        for (const P of loop) {
            position.push(P.x, P.y, P.z);
            normal.push(n.x, n.y, n.z);
            skinIndex.push(options.skinIndex ?? 0, 0, 0, 0);
            skinWeight.push(1, 0, 0, 0);
            vertexBindings.push({ type: "face", id: this.#id });
        }
        for (let i = 2; i < loop.length; i++) {
            index.push(0, i - 1, i);
            faceBindings.push({ type: "face", id: this.#id });
        }
        if (!noBevel && edgeWidth > 0) {
            return combineGeometryAttributes([
                {
                    position,
                    normal,
                    index,
                    skinIndex,
                    skinWeight,
                    faceBindings,
                    vertexBindings,
                },
                this.getBevelGeometryAttributes({
                    edgeWidth,
                    skinIndex: options.skinIndex,
                }),
            ]);
        }
        return {
            position,
            normal,
            index,
            skinIndex,
            skinWeight,
            faceBindings,
            vertexBindings,
        };
    }
    getBevelGeometryAttributes(options) {
        const { edgeWidth = 0 } = options;
        if (edgeWidth <= 0) {
            throw new Error("Edge width must be positive");
        }
        const position = [];
        const normal = [];
        const skinIndex = [];
        const skinWeight = [];
        const index = [];
        const faceBindings = [];
        const vertexBindings = [];
        const edgesLoops = this.getEdgeLoops();
        if (edgesLoops.length === 0) {
            throw new Error("Face has no edge loops");
        }
        if (edgesLoops.length > 1) {
            throw new Error("Face has multiple edge loops");
        }
        const n = this.getNormal();
        const addPoint = (edge, A) => {
            const i = Math.floor(position.length / 3);
            position.push(A.x, A.y, A.z);
            normal.push(n.x, n.y, n.z);
            skinIndex.push(options.skinIndex ?? 0, 0, 0, 0);
            skinWeight.push(1, 0, 0, 0);
            vertexBindings.push({ type: "edge", id: edge.id });
            return i;
        };
        const addFace = (edge, A, B, C, D) => {
            index.push(A, B, C);
            index.push(C, D, A);
            faceBindings.push({ type: "edge", id: edge.id });
            faceBindings.push({ type: "edge", id: edge.id });
        };
        const edgesLoop = edgesLoops[0];
        const loop = edgesLoop.map((edge) => {
            return this.getFirstVertex(edge).toAffine();
        });
        if (loop.length < 3) {
            throw new Error("Face has less than 3 vertices");
        }
        const innerLoop = Array.from(grow(loop, n, -edgeWidth));
        for (const [A0, A1, i] of pairs(loop)) {
            const A2 = innerLoop[(i + 1) % loop.length];
            const A3 = innerLoop[i];
            const edge = edgesLoop[i];
            addFace(edge, addPoint(edge, A0), addPoint(edge, A1), addPoint(edge, A2), addPoint(edge, A3));
        }
        return {
            position,
            normal,
            index,
            skinIndex,
            skinWeight,
            faceBindings,
            vertexBindings,
        };
    }
    // getEdgeGeometry() {
    //   return createEdgeGeometry(this.getEdgeGeometryAttributes());
    // }
    getEdgeGeometryAttributes() {
        const position = [];
        for (const [edge] of this.edges()) {
            const [v0, v1] = edge.vertices;
            const p0 = v0.toAffine();
            const p1 = v1.toAffine();
            position.push(p0.x, p0.y, p0.z);
            position.push(p1.x, p1.y, p1.z);
        }
        return {
            position,
        };
    }
}
//# sourceMappingURL=Face.js.map